import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { countries } from '@/constants/CountryCodes';
import { cn } from '@/lib/utils';

interface CountryCodeSelectProps {
  /** Dial code including the plus, e.g. "+964". */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  /** Extra trigger classes — lets a caller match a taller field row. */
  className?: string;
}

/**
 * Country dial-code picker with search.
 *
 * Typing matches the country name in the user's own language, the English
 * name, the ISO code, or the dial code — so "iraq", "العراق", "IQ" and "964"
 * all land on the same row.
 */
const CountryCodeSelect = ({
  value,
  onChange,
  disabled,
  readOnly,
  className,
}: CountryCodeSelectProps) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [panelWidth, setPanelWidth] = useState<number>();

  // Match the panel to the whole phone-number row rather than to the narrow
  // trigger, so the list lines up with the field above it instead of floating
  // at an arbitrary width.
  useLayoutEffect(() => {
    if (!open) return;
    const row = triggerRef.current?.parentElement;
    if (row) setPanelWidth(row.getBoundingClientRect().width);
  }, [open]);

  const options = useMemo(() => {
    // Intl gives us translated country names for free — no name table to ship
    // or keep in sync. Not every runtime has it, hence the guard.
    let display: Intl.DisplayNames | undefined;
    try {
      display = new Intl.DisplayNames([i18n.language], { type: 'region' });
    } catch {
      display = undefined;
    }

    const mapped = countries.map((country) => {
      let localName = country.name;
      try {
        localName = display?.of(country.code) ?? country.name;
      } catch {
        // Codes Intl doesn't recognise keep their English name.
      }
      return {
        ...country,
        localName,
        // cmdk both matches and de-duplicates on this string, so it carries
        // every spelling worth searching AND stays unique via the ISO code.
        search: `${localName} ${country.name} ${country.code} ${country.dialCode}`,
      };
    });

    // The current selection leads the list. Alphabetical-only means an Iraqi
    // merchant opens the picker on Afghanistan, and cmdk highlights whatever is
    // first — so the highlight lands on a country nobody asked for.
    const selectedIndex = mapped.findIndex((country) => `+${country.dialCode}` === value);
    if (selectedIndex > 0) {
      const [current] = mapped.splice(selectedIndex, 1);
      mapped.unshift(current);
    }
    return mapped;
  }, [i18n.language, value]);

  const selected = options.find((country) => `+${country.dialCode}` === value);

  // The phone row is always dir="ltr" (numbers read left-to-right in every
  // locale) so the trigger sits at its physical left. The panel is portalled to
  // <body> and inherits dir="rtl" from <html>, and floating-ui reads align from
  // that computed direction — so under Arabic "start" resolved to the RIGHT edge
  // and the panel hung 143px off the card. Flipping the token keeps the panel
  // physically left-aligned with the trigger in both locales.
  // (A DirectionProvider does not help here: @radix-ui/react-popover never
  // reads one.)
  const align = i18n.dir?.() === 'rtl' ? 'end' : 'start';

  return (
    <Popover open={readOnly ? false : open} onOpenChange={setOpen}>
      <PopoverTrigger
        ref={triggerRef}
        type='button'
        disabled={disabled || readOnly}
        aria-label={String(t('auth_country_code'))}
        className={cn(
          'flex h-12 w-[118px] shrink-0 items-center justify-between gap-1 rounded-xl border border-solid border-sd-input bg-sd-input-bg px-3 text-sm font-medium text-sd-foreground transition-colors hover:bg-sd-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring disabled:cursor-not-allowed disabled:opacity-60',
          readOnly && 'pointer-events-none text-sd-muted-foreground',
          className,
        )}
      >
        <span className='flex min-w-0 items-center gap-2'>
          <span aria-hidden className='w-5 shrink-0 text-center text-base leading-none'>
            {selected?.emoji}
          </span>
          <span dir='ltr' className='tabular-nums'>
            {value}
          </span>
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-sd-muted-foreground transition-transform duration-200',
            open && 'rotate-180',
          )}
          strokeWidth={2}
        />
      </PopoverTrigger>

      {/* `sd-scope` has to be repeated here: Radix portals this to <body>, well
          outside the scope wrapper, so without it the design tokens the shadcn
          Command styles reference resolve to nothing.
          The shadow is layered rather than shadcn's single `shadow-md` blur —
          a tight contact shadow plus a wide soft one is what stops a floating
          panel reading as a grey smudge. */}
      {/* Width goes through a CSS variable, not an inline `width`. This Tailwind
          config sets `important: true`, so PopoverContent's base `w-72` carries
          !important and beats any inline width — the panel silently stayed 288px.
          A static `w-[var(--cc-panel-w)]` class is important too, so it wins,
          and the variable lets it carry a measured value. */}
      <PopoverContent
        align={align}
        sideOffset={8}
        collisionPadding={16}
        style={
          {
            '--cc-panel-w': `min(${panelWidth ?? 300}px, calc(100vw - 2rem))`,
          } as React.CSSProperties
        }
        className='sd-scope sd-font box-border w-[var(--cc-panel-w)] overflow-hidden rounded-xl border-sd-border p-0 shadow-[0_4px_12px_-2px_rgba(16,24,40,0.10),0_20px_44px_-8px_rgba(16,24,40,0.18)]'
      >
        <Command>
          <CommandInput placeholder={String(t('auth_country_search'))} />
          {/* Capped against the viewport too: on a phone a fixed panel flips
              above the field and swallows the whole card. */}
          <CommandList className='max-h-[min(272px,38vh)] scroll-py-1 p-1.5'>
            <CommandEmpty>{String(t('auth_country_none'))}</CommandEmpty>
            {options.map((country) => {
              const isSelected = selected?.code === country.code;
              return (
                <CommandItem
                  key={country.code}
                  value={country.search}
                  onSelect={() => {
                    onChange(`+${country.dialCode}`);
                    setOpen(false);
                  }}
                  className='h-10 cursor-pointer gap-2.5 rounded-lg px-2.5'
                >
                  {/* Fixed-width box so every country name starts on the same
                      line regardless of how wide the platform draws the flag. */}
                  <span aria-hidden className='w-5 shrink-0 text-center text-base leading-none'>
                    {country.emoji}
                  </span>
                  <span className={cn('min-w-0 flex-1 truncate', isSelected && 'font-semibold')}>
                    {country.localName}
                  </span>
                  {/* Fixed width + tabular figures: the dial codes line up as a
                      column instead of jittering with each name's length. The
                      tick sits after them so the trailing edge reads as one
                      column, not a mark floating in the middle of the row. */}
                  <span
                    dir='ltr'
                    className='w-12 shrink-0 text-end text-[13px] tabular-nums text-sd-muted-foreground'
                  >
                    +{country.dialCode}
                  </span>
                  <span className='flex w-4 shrink-0 justify-center'>
                    {isSelected ? (
                      <Check className='h-4 w-4 text-sd-foreground' strokeWidth={2.5} />
                    ) : null}
                  </span>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CountryCodeSelect;
